import { useEffect, useMemo, useRef, useState } from "react";
import { App, ConfigProvider, Modal } from "antd";
import { ExclamationCircleFilled, WalletOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Deposit } from "../../../../../api/deposits";
import { useDefaultCompanyInvoiceIssuing } from "../../../../../hooks/useCompanies";
import { useActivePaymentMethods } from "../../../../../hooks/usePaymentMethods";
import { softwareInvoicesAllowedIn } from "../../../../../lib/fiscalInvoice";
import { useViewport } from "../../../../../hooks/useViewport";
import { useAppSelector, useThemePalette } from "../../../../../store/hooks";
import { formatCents, parseMoneyToCents } from "../../../../../utils/money";
import {
  GhostButton,
  ModalFooter,
  ModalHeader,
  PrimaryButton,
  posModalStyles as modalStyles,
} from "../../modalPrimitives";
import { useOrderingMessage } from "../../useOrderingMessage";
import { NoticeBanner } from "../invoicing/NoticeBanner";
import { takeKeyFromField } from "../listKeyboard";
import { DepositDetailsColumn, type DepositDetailsDraft } from "./DepositDetailsColumn";
import { DepositMethodList } from "./DepositMethodList";
import { depositReceiptNoticeOf } from "./depositReceiptNotice";
import { depositSaveLabelKey, useDepositSave } from "./useDepositSave";

/** Депозит не се взема със „Смесено" и „По сметка" (BE `NOT_DEPOSIT_METHOD_IDS`). */
const NOT_DEPOSIT_METHOD_IDS = new Set([3, 4]);
const CASH_PAYMENT_METHOD_ID = 1;
const CARD_PAYMENT_METHOD_ID = 2;
/** Таванът на един депозит (BE `MAX_DEPOSIT_AMOUNT`). */
const MAX_DEPOSIT_CENTS = 99_999_999;

interface DepositLoadModalProps {
  open: boolean;
  onClose: () => void;
  /** Записаният депозит с готов бон (`printed`/`not_required`); `withInvoice`
   *  — следва фактурата за него. */
  onSaved: (deposit: Deposit, withInvoice: boolean) => void;
}

const emptyDraft = (): DepositDetailsDraft => ({
  name: "",
  amountText: "",
  description: "",
  validUntil: null,
  withInvoice: false,
});

/**
 * „Депозити" (Маркиране → Опции): пари, взети ПРЕДИ сметката. Подреден като
 * „Закрий": вляво какво се взема (име, сума, „За дата" — след нея депозитът е
 * невалиден, описание, „Фактуриране"), вдясно — с какъв метод. Записът издава
 * касов бон „ЧАСТИЧНИ ПЛАЩАНИЯ" (Бон 1, Н-18 чл. 26а), освен ако методът е без
 * фискализиране (`print_route = pos`); изходите на бона и повторът са в
 * `useDepositSave.ts`. С „Фактуриране" след готовия бон се отваря фактурата за
 * депозита (`onSaved`) — без нов бон. Взетият депозит влиза в персоналния
 * отчет на взелия, а с него после се плаща сметка в „Закрий".
 */
export function DepositLoadModal({ open, onClose, onSaved }: DepositLoadModalProps) {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { modal } = App.useApp();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const themePalette = useThemePalette();
  const permissions = useAppSelector((state) => state.auth.permissions);
  const operatorName = useAppSelector((state) => state.auth.names);
  const hasPermission = (name: string) =>
    (permissions ?? []).some((permission) => permission.name === name);
  const { data: activeMethods = [] } = useActivePaymentMethods();
  const canInvoice = hasPermission("invoicing");
  // Фирмата е във „Фискално устройство": софтуерна фактура за депозита няма
  // (BE я отказва) — „Фактуриране" стои сиво с причината.
  const invoiceIssuing = useDefaultCompanyInvoiceIssuing({ enabled: open && canInvoice });
  const invoiceBlockedReason = softwareInvoicesAllowedIn(invoiceIssuing)
    ? null
    : t("fiscalInvoice.mode.softwareOff");

  const [draft, setDraft] = useState<DepositDetailsDraft>(emptyDraft);
  const [methodId, setMethodId] = useState<number | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const handleSaveRef = useRef<() => void>(() => {});
  // „Фактуриране" може да се смени и докато бонът чака — решава моментът на
  // готовия бон, не на натискането.
  const withInvoiceRef = useRef(false);
  withInvoiceRef.current = draft.withInvoice && !invoiceBlockedReason;
  const depositSave = useDepositSave({
    onReady: (deposit) => onSaved(deposit, withInvoiceRef.current),
  });
  const { reset: resetSave, isPending } = depositSave;
  // След първия опит депозитът е (или може да е) записан — сумата и методът
  // са тези на записа и не се сменят.
  const locked = depositSave.phase !== "draft";

  const methods = useMemo(
    () => activeMethods.filter((method) => !NOT_DEPOSIT_METHOD_IDS.has(method.id)),
    [activeMethods],
  );
  const isMethodDisabled = (id: number) =>
    locked
      ? id !== methodId
      : (id === 1 && !hasPermission("payment_cash")) || (id === 2 && !hasPermission("payment_card"));
  // Методът е без фискализиране за принтера на взелия — бон по закон няма.
  const withoutReceipt =
    methods.find((method) => method.id === methodId)?.print_route === "pos";
  const notice = depositReceiptNoticeOf({
    phase: depositSave.phase,
    receiptState: depositSave.receiptState,
    withoutReceipt,
    isCard: methodId === CARD_PAYMENT_METHOD_ID,
  });

  useEffect(() => {
    if (!open) return;
    setDraft(emptyDraft());
    setMethodId(null);
    setShowErrors(false);
    resetSave();
  }, [open, resetSave]);

  // По подразбиране „В брой" (или първият позволен метод).
  useEffect(() => {
    if (!open || methodId !== null) return;
    const allowed = methods.filter((method) => !isMethodDisabled(method.id));
    const preferred = allowed.find((method) => method.id === CASH_PAYMENT_METHOD_ID) ?? allowed[0];
    if (preferred) setMethodId(preferred.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, methods, methodId]);

  // Клавиатурата като в „Закрий" (десктоп): стрелките сменят метода, Enter
  // записва — но не докато се пише в поле (там Enter е на самото поле); от
  // „Име" (с фокус при отваряне) и „Сума" ↑ ↓ излизат и местят метода
  // (`listKeyboard.ts`). „Затваряне без потвърждение?" отгоре — клавишите са негови.
  const closeConfirmOpenRef = useRef(false);

  useEffect(() => {
    if (!open || !isDesktop) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (closeConfirmOpenRef.current) return;
      if (!takeKeyFromField(event)) return;
      if (event.key === "Enter") {
        event.preventDefault();
        handleSaveRef.current();
      } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        // Само ↑ ↓ — ← → са на „Фактуриране" (antd Switch), когато е на фокус.
        if (locked) return;
        const allowed = methods.filter((method) => !isMethodDisabled(method.id));
        if (allowed.length === 0) return;
        event.preventDefault();
        const index = allowed.findIndex((method) => method.id === methodId);
        const nextIndex =
          event.key === "ArrowDown"
            ? (index + 1) % allowed.length
            : index <= 0
              ? allowed.length - 1
              : index - 1;
        setMethodId(allowed[nextIndex].id);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isDesktop, methods, methodId, locked]);

  const amountCents = parseMoneyToCents(draft.amountText);
  const amountValid = amountCents !== null && amountCents > 0 && amountCents <= MAX_DEPOSIT_CENTS;

  // Затварянето: без отговор от сървъра депозитът може вече да е записан —
  // ново отваряне е нов ключ, а въведен наново би станал втори депозит, затова
  // първо въпрос. Записан депозит без бон затваря с напомняне: бонът се
  // издава после („Издай бон").
  const requestClose = () => {
    if (isPending || closeConfirmOpenRef.current) return;
    if (depositSave.phase === "noAnswer") {
      closeConfirmOpenRef.current = true;
      modal.confirm({
        title: t("depositLoad.closeConfirmTitle"),
        content: t("depositLoad.closeConfirmBody"),
        icon: <ExclamationCircleFilled className="text-[var(--color-danger-ink)]" />,
        centered: true,
        okText: t("depositLoad.closeConfirmOk"),
        okButtonProps: { danger: true },
        cancelText: t("depositLoad.closeConfirmStay"),
        onOk: onClose,
        afterClose: () => {
          closeConfirmOpenRef.current = false;
        },
      });
      return;
    }
    if (depositSave.phase === "saved") message.warning(t("depositLoad.closedWithoutReceipt"));
    onClose();
  };

  const handleSave = async () => {
    if (isPending) return;
    setShowErrors(true);
    if (!draft.name.trim()) {
      message.warning(t("depositLoad.errName"));
      return;
    }
    if (!amountValid) {
      message.warning(t("depositLoad.errAmount"));
      return;
    }
    if (!methodId) {
      message.warning(t("depositLoad.errMethod"));
      return;
    }
    await depositSave.submit({
      name: draft.name.trim(),
      amount: formatCents(amountCents ?? 0),
      description: draft.description.trim() || null,
      paymentMethodId: methodId,
      validUntil: draft.validUntil ? draft.validUntil.format("YYYY-MM-DD") : null,
    });
  };
  handleSaveRef.current = () => void handleSave();

  // Широк модал като „Закрий" — двете тонирани колони искат въздух.
  const width = isMobile ? undefined : isTablet ? 560 : 880;
  const fieldPaddingInline = isNarrow ? 12 : 16;

  return (
    // Локална antd тема (разрешеното изключение от „само Tailwind"): antd
    // полетата (дата, описание, фактурата) с кутията на „Име"/„Сума" —
    // фонът и рамката са pos-card/pos-line в модал (тъмните: ниво elevated).
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: themePalette.posAccent,
          colorBgContainer: themePalette.elevated?.surface ?? themePalette.surface,
          colorBorder: themePalette.elevated?.border ?? themePalette.border,
          // Полетата с размера на полето за сума в „Закрий" (`CLOSE_FIELD_BOX`:
          // 54px и rounded-xl; на телефон — колкото методите, 43px и 10px).
          controlHeight: isNarrow ? 43 : 54,
          borderRadius: isNarrow ? 10 : 12,
        },
        // Само в полетата (не в целия модал — редовете вдясно са 1:1 със
        // „Закрий"): текстът като в „Име" — 16px (и заради iOS Safari: под 16
        // зуумва при фокус) и навътре колкото него (`pl-4`, на телефон `px-3`);
        // Select-ът няма свой токен за отстъпа.
        components: {
          Input: { fontSize: 16, paddingInline: fieldPaddingInline },
          DatePicker: { fontSize: 16, paddingInline: fieldPaddingInline },
          Select: { fontSize: 16, paddingSM: fieldPaddingInline + 1 },
        },
      }}
    >
      <Modal
        open={open}
        onCancel={requestClose}
        footer={null}
        centered
        closable={false}
        destroyOnHidden
        width={width}
        styles={modalStyles}
        style={{ maxWidth: "calc(100vw - 32px)" }}
      >
        <div className="flex max-h-[90vh] flex-col bg-pos-card font-pos-sans text-pos-ink">
          <ModalHeader
            icon={<WalletOutlined />}
            title={
              // Записан ли е — номерът му (id-то; само показване).
              depositSave.savedDeposit
                ? t("depositLoad.titleSaved", { id: depositSave.savedDeposit.id })
                : t("depositLoad.title")
            }
            subtitle={`${t("closeTable.operator")} ${operatorName || "—"}`}
            onClose={requestClose}
            isNarrow={isNarrow}
            tone="panel"
          />

          {notice && (
            <div
              className={
                isNarrow ? "px-4 pt-3" : "border-b border-pos-line2 bg-pos-card px-5 py-3"
              }
            >
              <NoticeBanner
                tone={notice.tone}
                text={t(notice.textKey, {
                  error: depositSave.receiptError || t("depositReceipt.noDetails"),
                })}
              />
            </div>
          )}

          <div
            className={
              isNarrow
                ? "flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-3.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                : // Широк екран: две тонирани колони, допрени на вертикален
                  // разделител — паддингът е ВЪТРЕ в колоните (като „Закрий")
                  "flex min-h-0 flex-1 items-stretch"
            }
          >
            {/* Колоните са тези на „Закрий" с бакшиш: 55 % вляво, методите вдясно;
                скролът (с фактурата) е без видима лента, като в Маркиране */}
            <div
              className={
                isNarrow
                  ? "flex min-w-0 flex-col"
                  : "flex w-[55%] min-w-0 flex-col overflow-y-auto bg-pos-canvas px-5 py-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              }
            >
              <DepositDetailsColumn
                draft={draft}
                onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
                amountValid={amountValid}
                canInvoice={canInvoice}
                invoiceBlockedReason={invoiceBlockedReason}
                showErrors={showErrors}
                onSubmit={() => void handleSave()}
                isNarrow={isNarrow}
                isCompact={isTablet && !isNarrow}
                isDesktop={isDesktop}
                locked={locked}
              />
            </div>

            <div
              className={
                isNarrow
                  ? "min-w-0"
                  : "min-w-0 flex-1 overflow-y-auto border-l border-pos-line2 bg-pos-marked px-5 py-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              }
            >
              <DepositMethodList
                methods={methods}
                selectedId={methodId}
                onSelect={(id) => {
                  if (!locked) setMethodId(id);
                }}
                isMethodDisabled={isMethodDisabled}
                isNarrow={isNarrow}
              />
            </div>
          </div>

          <ModalFooter isNarrow={isNarrow} tone="panel" narrowFirst>
            <GhostButton
              label={t("common.cancel")}
              onClick={requestClose}
              disabled={isPending}
              shortcut={isDesktop ? "Esc" : undefined}
            />
            <PrimaryButton
              label={
                <>
                  {t(depositSaveLabelKey(depositSave.phase, depositSave.receiptState))}
                  {amountValid && (
                    <>
                      {" · "}
                      <span className="font-pos-mono">{formatCents(amountCents ?? 0)} EUR</span>
                    </>
                  )}
                </>
              }
              onClick={() => void handleSave()}
              loading={isPending}
              shortcut={isDesktop ? "⏎" : undefined}
            />
          </ModalFooter>
        </div>
      </Modal>
    </ConfigProvider>
  );
}
